import { SVGInternalIcon } from "@components:SVGInternalIcon";
import { useNewChat } from "@hooks:useNewChat";
import { useRBACCheck } from "@hooks:useRBAC";
import { Box, IconButton } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { useSidebarActions } from "@stores:sidebar";
import { useLocation, useNavigate } from "react-router";

import { FeatureFlag } from "@:flag";
import { RBACAction, RBACResource } from "@:rbac";

type SwitchPageProps = {
  disablePageClick?: boolean;
};

export const SwitchPage = ({ disablePageClick = true }: SwitchPageProps) => {
  const navigate = useNavigate();
  const { newChat } = useNewChat();
  const canAccessAnalytics = useRBACCheck(
    RBACAction.read,
    RBACResource.analytics,
  );
  const sidebarActions = useSidebarActions();

  const { pathname } = useLocation();

  const pages: Record<string, string> = {
    chat: "/app/chat",
    search: "/app/search",
    docs: "/app/docs",
    analytics: "/app/analytics",
    audio: "/app/audio",
  };

  const disablePage = Object.keys(pages).reduce(
    (acc, key) => {
      acc[key] = pathname.includes(pages[key]) && disablePageClick;
      return acc;
    },
    {} as Record<keyof typeof pages, boolean>,
  );

  return (
    <Box>
      <Tooltip
        title={
          disablePage.chat || disablePage.search ? null : "Go to nemo talk"
        }
      >
        <span>
          <IconButton
            disabled={disablePage.chat || disablePage.search}
            sx={{ width: "50px", height: "50px" }}
            onClick={() => {
              newChat();
              sidebarActions.showSidebar(false);
            }}
          >
            <SVGInternalIcon
              icon="botNav"
              sx={{
                width: 28,
                height: 28,
                opacity: disablePage.chat || disablePage.search ? 0.3 : 1,
              }}
            />
          </IconButton>
        </span>
      </Tooltip>
      {FeatureFlag.Docs && (
        <Tooltip title={disablePage.docs ? null : "Go to nemo docs"}>
          <span>
            <IconButton
              onClick={() => {
                navigate("/app/docs", { replace: true });
              }}
              disabled={disablePage.docs}
              sx={{ width: "50px", height: "50px" }}
            >
              <SVGInternalIcon
                icon="docsNav"
                sx={{
                  width: 28,
                  height: 28,
                  opacity: disablePage.docs ? 0.3 : 1,
                }}
              />
            </IconButton>
          </span>
        </Tooltip>
      )}

      <Tooltip title={disablePage.audio ? null : "Go to nemo audio"}>
        <span>
          <IconButton
            onClick={() => {
              navigate("/app/audio", { replace: true });
            }}
            disabled={disablePage.audio}
            sx={{ width: "50px", height: "50px" }}
          >
            <SVGInternalIcon
              icon="audio"
              sx={{
                width: 28,
                height: 28,
                opacity: disablePage.audio ? 0.3 : 1,
              }}
            />
          </IconButton>
        </span>
      </Tooltip>

      {FeatureFlag.Analytics && canAccessAnalytics && (
        <Tooltip title={disablePage.analytics ? null : "Go to nemo analytics"}>
          <span>
            <IconButton
              disabled={disablePage.analytics}
              onClick={() => {
                navigate("/app/analytics", { replace: true });
              }}
            >
              <SVGInternalIcon
                icon="analyticsNav"
                sx={{
                  width: 23,
                  height: 23,
                  opacity: disablePage.analytics ? 0.3 : 1,
                }}
              />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );
};
